import React, { useState, useRef, useEffect } from 'react';
import { Share2, Link2, Facebook, Twitter, Linkedin, Mail, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const ShareButtons = ({ type, slug, title, description }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  // Detect touch / mobile devices
  const isMobile = typeof window !== 'undefined' && (
    /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints && navigator.maxTouchPoints > 1)
  );
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const siteUrl = process.env.REACT_APP_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : 'https://exploremhc.com');
  const pageUrl = `${siteUrl}/${type === 'business' ? 'businesses' : 'events'}/${slug}`;
  const shareText = title ? `Check out ${title} on Explore MHC` : 'Check this out on Explore MHC';
  const subject = title ? `Check out ${title} on Explore MHC` : 'Found this on Explore MHC';
  const bodyText = `${description ? description.substring(0, 200) + '\n\n' : ''}${pageUrl}`;

  const links = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(pageUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`,
    email: `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyText)}`,
    sms: `sms:?&body=${encodeURIComponent(`${shareText}: ${pageUrl}`)}`,
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      toast.success('Link copied to clipboard!');
      setOpen(false);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  // Try native share first (opens iOS/Android share sheet → user picks FB/IG/Messages/etc.)
  const handleMainClick = async () => {
    if (canNativeShare && isMobile) {
      try {
        await navigator.share({
          title: title || 'Explore MHC',
          text: description?.substring(0, 200) || shareText,
          url: pageUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          // Fallback to dropdown if native share failed
          setOpen(true);
        }
      }
    } else {
      setOpen(o => !o);
    }
  };

  // Open share URL — same tab on mobile (avoids popup blockers), new tab on desktop
  const openShareUrl = (url) => {
    setOpen(false);
    if (isMobile) {
      window.location.href = url;
    } else {
      // Use noopener for security. Note: returns null with noopener,
      // so we cannot reliably detect popup-blocker state.
      window.open(url, '_blank', 'noopener,noreferrer,width=720,height=640');
    }
  };

  const Item = ({ Icon, label, onClick, color }) => (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-100 transition-colors text-left"
    >
      <Icon className={`w-4 h-4 flex-shrink-0 ${color || 'text-neutral-500'}`} />
      <span>{label}</span>
    </button>
  );

  return (
    <div className="relative inline-block" ref={ref} data-testid="share-buttons">
      <button
        type="button"
        onClick={handleMainClick}
        className="flex items-center gap-2 px-4 py-2 rounded-lg border border-neutral-200 text-neutral-700 hover:bg-neutral-100 transition-colors text-sm font-medium"
        data-testid="share-toggle-btn"
      >
        <Share2 className="w-4 h-4" />
        Share
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-56 bg-white border border-neutral-200 rounded-xl shadow-lg overflow-hidden z-50 py-1"
          data-testid="share-menu"
        >
          <Item Icon={Facebook} label="Facebook" onClick={() => openShareUrl(links.facebook)} color="text-[#1877F2]" />
          <Item Icon={Twitter} label="X (Twitter)" onClick={() => openShareUrl(links.twitter)} color="text-neutral-900" />
          <Item Icon={Linkedin} label="LinkedIn" onClick={() => openShareUrl(links.linkedin)} color="text-[#0A66C2]" />
          <Item Icon={Mail} label="Email" onClick={() => openShareUrl(links.email)} color="text-amber-600" />
          <Item Icon={MessageCircle} label="Text / SMS" onClick={() => openShareUrl(links.sms)} color="text-green-600" />
          <div className="border-t border-neutral-100 my-1" />
          <Item Icon={Link2} label="Copy link" onClick={copyToClipboard} />
        </div>
      )}
    </div>
  );
};

export default ShareButtons;
